import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { useAdminScope } from "../../context/AdminScopeContext";
import { useAdminConfirmation } from "../../pages/admin/panel_engine/consumer";
import { usePanelActions } from "../../pages/admin/panel_engine/consumer";
import { AdminEmptyState, AdminErrorState, AdminLoadingState } from "../../pages/admin/ui_engine";
import {
  DEFAULT_COMMERCIAL_RULE_LIMIT,
  createCommercialRule,
  deleteCommercialRule,
  getCommercialRuleErrorMessage,
  listCommercialRules,
  updateCommercialRule,
} from "../../services/supplierCommercialRulesAdminApi";

import CommercialRuleForm from "./supplier_commercial_rules/CommercialRuleForm";
import CommercialRulesList from "./supplier_commercial_rules/CommercialRulesList";

export const COMMERCIAL_RULES_PANEL_STATUS = Object.freeze({
  LOADING: "loading",
  EMPTY: "empty",
  ERROR: "error",
  READY: "ready",
});

const EMPTY_PAGINATION = Object.freeze({
  offset: 0,
  limit: DEFAULT_COMMERCIAL_RULE_LIMIT,
  total: 0,
});

function normalizeResponse(payload) {
  return {
    rules: Array.isArray(payload?.commercial_rules) ? payload.commercial_rules : [],
    pagination: { ...EMPTY_PAGINATION, ...(payload?.pagination || {}) },
  };
}

function buildUpdatePayload(initialRule, values) {
  const payload = {};
  if (values.rule_type !== initialRule.rule_type) payload.rule_type = values.rule_type;
  if (values.priority !== initialRule.priority) payload.priority = values.priority;
  if (JSON.stringify(values.configuration) !== JSON.stringify(initialRule.configuration || {})) {
    payload.configuration = values.configuration;
  }
  if (values.active !== initialRule.active) payload.active = values.active;
  return payload;
}

export default function SupplierCommercialRulesAdminPanel({
  contractUuid,
  contractLabel = "",
  supplierLabel = "",
}) {
  const { selectedCompanyUuid } = useAdminScope();
  const confirm = useAdminConfirmation();
  const [status, setStatus] = useState(COMMERCIAL_RULES_PANEL_STATUS.LOADING);
  const [rules, setRules] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [ruleTypeFilter, setRuleTypeFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [mutationError, setMutationError] = useState("");
  const [formMode, setFormMode] = useState(null);
  const [selectedRule, setSelectedRule] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingRuleUuid, setDeletingRuleUuid] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadRules = useCallback(async ({
    requestedOffset = 0,
    requestedRuleType = ruleTypeFilter,
    preserveData = false,
  } = {}) => {
    if (typeof contractUuid !== "string" || !contractUuid.trim()) {
      setStatus(COMMERCIAL_RULES_PANEL_STATUS.ERROR);
      setErrorMessage("Aucun contrat fournisseur n’est sélectionné.");
      return;
    }

    preserveData ? setRefreshing(true) : setStatus(COMMERCIAL_RULES_PANEL_STATUS.LOADING);
    setErrorMessage("");

    try {
      const payload = await listCommercialRules(contractUuid, {
        companyUuid: selectedCompanyUuid,
        offset: requestedOffset,
        limit: DEFAULT_COMMERCIAL_RULE_LIMIT,
        ruleType: requestedRuleType,
      });
      const normalized = normalizeResponse(payload);
      setRules(normalized.rules);
      setPagination(normalized.pagination);
      setStatus(normalized.rules.length
        ? COMMERCIAL_RULES_PANEL_STATUS.READY
        : COMMERCIAL_RULES_PANEL_STATUS.EMPTY);
    } catch (error) {
      if (!preserveData) {
        setRules([]);
        setPagination(EMPTY_PAGINATION);
        setStatus(COMMERCIAL_RULES_PANEL_STATUS.ERROR);
      }
      setErrorMessage(getCommercialRuleErrorMessage(
        error,
        "Impossible de charger les règles commerciales.",
      ));
    } finally {
      setRefreshing(false);
    }
  }, [contractUuid, ruleTypeFilter, selectedCompanyUuid]);

  useEffect(() => {
    loadRules({ requestedOffset: 0, requestedRuleType: ruleTypeFilter });
  }, [loadRules, ruleTypeFilter]);

  const openCreate = useCallback(() => {
    setSelectedRule(null);
    setMutationError("");
    setFormMode("create");
  }, []);

  const openEdit = useCallback((rule) => {
    setSelectedRule(rule);
    setMutationError("");
    setFormMode("edit");
  }, []);

  const refresh = useCallback(() => loadRules({
    requestedOffset: pagination.offset,
    requestedRuleType: ruleTypeFilter,
    preserveData: rules.length > 0,
  }), [loadRules, pagination.offset, ruleTypeFilter, rules.length]);

  usePanelActions(useMemo(() => [
    {
      key: "create-commercial-rule",
      label: "Créer une règle",
      icon: "+",
      onClick: openCreate,
      disabled: submitting || Boolean(deletingRuleUuid),
    },
    {
      key: "refresh-commercial-rules",
      label: refreshing ? "Actualisation en cours" : "Actualiser les règles",
      icon: "↻",
      onClick: refresh,
      disabled: refreshing || submitting,
    },
  ], [deletingRuleUuid, openCreate, refresh, refreshing, submitting]));

  const closeForm = () => {
    if (submitting) return;
    setFormMode(null);
    setSelectedRule(null);
    setMutationError("");
  };

  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);
    setMutationError("");
    try {
      if (formMode === "edit" && selectedRule) {
        const payload = buildUpdatePayload(selectedRule, values);
        if (Object.keys(payload).length) {
          await updateCommercialRule(contractUuid, selectedRule.uuid, payload, {
            companyUuid: selectedCompanyUuid,
          });
        }
      } else {
        await createCommercialRule(contractUuid, values, {
          companyUuid: selectedCompanyUuid,
        });
      }
      setFormMode(null);
      setSelectedRule(null);
      await loadRules({
        requestedOffset: pagination.offset,
        requestedRuleType: ruleTypeFilter,
        preserveData: true,
      });
    } catch (error) {
      setMutationError(getCommercialRuleErrorMessage(
        error,
        "Impossible d’enregistrer la règle commerciale.",
      ));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (rule) => {
    if (deletingRuleUuid) return;
    const accepted = await confirm({
      title: "Supprimer la règle commerciale",
      message: `Confirmer la suppression de la règle « ${rule.rule_type} » ?`,
      confirmLabel: "Supprimer",
      cancelLabel: "Annuler",
      intent: "danger",
    });
    if (!accepted) return;

    setDeletingRuleUuid(rule.uuid);
    setMutationError("");
    try {
      await deleteCommercialRule(contractUuid, rule.uuid, {
        companyUuid: selectedCompanyUuid,
      });
      const targetOffset = rules.length === 1 && pagination.offset > 0
        ? Math.max(pagination.offset - pagination.limit, 0)
        : pagination.offset;
      await loadRules({
        requestedOffset: targetOffset,
        requestedRuleType: ruleTypeFilter,
        preserveData: rules.length > 1,
      });
    } catch (error) {
      setMutationError(getCommercialRuleErrorMessage(
        error,
        "Impossible de supprimer la règle commerciale.",
      ));
    } finally {
      setDeletingRuleUuid(null);
    }
  };

  const hasPrevious = pagination.offset > 0;
  const hasNext = pagination.offset + pagination.limit < pagination.total;
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.max(Math.ceil(pagination.total / pagination.limit), 1);

  return (
    <Root>
      {(supplierLabel || contractLabel) && (
        <Context>
          {supplierLabel && <span>Fournisseur : <strong>{supplierLabel}</strong></span>}
          {contractLabel && <span>Contrat : <strong>{contractLabel}</strong></span>}
        </Context>
      )}

      <Toolbar>
        <label htmlFor="commercial-rule-type-filter">Type de règle</label>
        <select
          id="commercial-rule-type-filter"
          value={ruleTypeFilter}
          onChange={(event) => setRuleTypeFilter(event.target.value)}
          disabled={submitting}
        >
          <option value="">Tous les types</option>
          <option value="unit_discount">Remise unitaire</option>
          <option value="category_discount">Remise par catégorie</option>
          <option value="case_pack_discount">Remise au colisage</option>
          <option value="until_case_pack_minus_one_discount">Remise jusqu’à colisage - 1</option>
          <option value="tier_discount">Remise par paliers</option>
          <option value="free_unit">Unités offertes</option>
        </select>
      </Toolbar>

      {mutationError && <MutationError role="alert">{mutationError}</MutationError>}
      {status === COMMERCIAL_RULES_PANEL_STATUS.LOADING && (
        <AdminLoadingState message="Chargement des règles commerciales..." />
      )}
      {status === COMMERCIAL_RULES_PANEL_STATUS.ERROR && (
        <AdminErrorState message={errorMessage} compact />
      )}
      {status === COMMERCIAL_RULES_PANEL_STATUS.EMPTY && (
        <AdminEmptyState
          message={ruleTypeFilter
            ? "Aucune règle ne correspond à ce type."
            : "Aucune règle commerciale n’est configurée pour ce contrat."}
          actionLabel="Créer une règle"
          onAction={openCreate}
        />
      )}
      {status === COMMERCIAL_RULES_PANEL_STATUS.READY && (
        <>
          <Summary><strong>{pagination.total}</strong> règle{pagination.total > 1 ? "s" : ""}{refreshing ? " — actualisation…" : ""}</Summary>
          <CommercialRulesList
            rules={rules}
            deletingRuleUuid={deletingRuleUuid}
            onEdit={openEdit}
            onDelete={handleDelete}
          />
          <Pagination>
            <button
              type="button"
              disabled={!hasPrevious || refreshing}
              onClick={() => loadRules({
                requestedOffset: Math.max(pagination.offset - pagination.limit, 0),
                requestedRuleType: ruleTypeFilter,
              })}
            >Page précédente</button>
            <span>Page {currentPage} / {totalPages}</span>
            <button
              type="button"
              disabled={!hasNext || refreshing}
              onClick={() => loadRules({
                requestedOffset: pagination.offset + pagination.limit,
                requestedRuleType: ruleTypeFilter,
              })}
            >Page suivante</button>
          </Pagination>
        </>
      )}

      {formMode && (
        <CommercialRuleForm
          mode={formMode}
          initialRule={selectedRule}
          submitting={submitting}
          errorMessage={mutationError}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}
    </Root>
  );
}

const Root = styled.section`min-width: 0; color: #f7f7f7;`;
const Context = styled.div`
  display: flex; flex-wrap: wrap; gap: .5rem 1.25rem; margin-bottom: 1rem;
  padding: .8rem 1rem; border: 1px solid rgba(255,255,255,.08);
  border-radius: 12px; background: rgba(255,255,255,.04);
  color: rgba(247,247,247,.72);
`;
const Toolbar = styled.div`
  display: flex; align-items: center; flex-wrap: wrap; gap: .65rem; margin-bottom: 1rem;
  label { font-weight: 750; color: rgba(247,247,247,.72); }
  select { min-height: 42px; padding: .65rem .8rem; border: 1px solid rgba(255,255,255,.13); border-radius: 12px; background: #151d31; color: #f7f7f7; }
`;
const MutationError = styled.div`
  margin-bottom: 1rem; padding: .75rem .9rem; border-radius: 12px;
  background: rgba(225,95,95,.12); color: #ffb4b4;
`;
const Summary = styled.p`margin: 0 0 .75rem; color: rgba(247,247,247,.68);`;
const Pagination = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: .75rem; margin-top: 1rem;
  button { min-height: 42px; padding: .65rem .9rem; border: 1px solid rgba(255,255,255,.13); border-radius: 12px; background: #202a43; color: #f7f7f7; font-weight: 750; }
  button:disabled { opacity: .45; }
  span { color: rgba(247,247,247,.68); font-size: .85rem; }
`;
